import { renderPageHeader } from "../ui/pageHeader.js";
import { toast } from "../ui/toast.js";
import { confirmModal } from "../ui/confirmModal.js";

function isProTier() {
    const u = window.__auth__ || {};
    if (['admin', 'angel_admin', 'god_admin'].includes(u.role)) return true;
    // Mirror server/middleware/tier.js MCP_ALLOWED_TIERS — trial users
    // get full Pro access during their evaluation window.
    return ['trial', 'pro', 'business', 'founder', 'agency'].includes(u.tier);
}

// ─── inline icons (Lucide style) ────────────────────────────────────
// Small icons (14px) for tabs/chips, large icons (48px) for empty states.
const _sm = (inner) => `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
const _lg = (inner) => `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
const ICON_CLOCK = _sm('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>');
const ICON_CHECK_CIRCLE = _sm('<circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/>');
const ICON_X_CIRCLE = _sm('<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>');
const ICON_ZAP = _sm('<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/>');
const ICON_LAYERS = _sm('<path d="m12 3 9 5-9 5-9-5 9-5z"/><path d="m3 13 9 5 9-5"/><path d="m3 18 9 5 9-5"/>');
const ICON_CALENDAR_SM = _sm('<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/>');
const ICON_PASTE = _sm('<rect x="8" y="3" width="8" height="4" rx="1"/><path d="M8 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>');
const ICON_SPARKLE_LG = _lg('<path d="M12 3v6M12 15v6M3 12h6M15 12h6M6 6l3 3M15 15l3 3M18 6l-3 3M6 18l3-3"/>');
const ICON_CHECK_LG = _lg('<circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/>');
const ICON_X_LG = _lg('<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>');
const ICON_ZAP_LG = _lg('<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/>');
const ICON_INBOX_LG = _lg('<path d="M22 13v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6"/><path d="M2 13h6l2 3h4l2-3h6"/><path d="M5.45 5.11 2 13M18.55 5.11 22 13"/>');
const ICON_LIST = _sm('<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>');
const ICON_GRID = _sm('<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>');
const ICON_CHEVRON = _sm('<polyline points="6 9 12 15 18 9"/>');
const ICON_TRASH = _sm('<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>');

/**
 * Agent Inbox — review & approve drafts created by external LLMs
 * (Claude Desktop, Cursor, ChatGPT, Copilot, etc.) via MCP / API key.
 */
export class AgentInboxPage {
    constructor(opts = {}) {
        // When embedded inside the Settings shell, suppress the standalone
        // page-header so it doesn't double up with the parent's pane head.
        this.embedded = !!opts.embedded;
        // Persisted view preference: 'list' (compact rows) or 'card' (roomy tiles)
        let savedViewMode = 'card';
        try { savedViewMode = localStorage.getItem('heist-inbox-view') || 'card'; } catch (_) {}
        this.state = {
            loading: true,
            error: null,
            filter: 'pending',
            items: [],
            counts: { pending: 0, approved: 0, rejected: 0, executed: 0 },
            selected: new Set(),
            expanded: new Set(),        // item ids whose bodies are open
            viewMode: savedViewMode,    // 'list' | 'card'
            editing: null, // item id being edited inline
            editBuffer: '',
            rejectReason: 'off-voice',
            rejectNote: '',
            importOpen: false,
            importText: '',
            importSource: 'agent:gemini_paste',
            importBusy: false,

            // Phase D1: Brain-write approval mode, synced with the AI Settings
            // toggle via /api/settings/brain-write-approval-mode.
            approvalMode: 'review',
            approvalPopoverOpen: false,
            approvalSaving: false,
            approvalSaveStatus: '',
        };
        this.el = null;
    }

    async mount(el) {
        this.el = el;
        this.render();
        this.attachListeners();
        this.loadApprovalMode();
        await this.load();
    }

    async loadApprovalMode() {
        try {
            const res = await fetchJson('/api/settings/brain-write-approval-mode');
            if (res && (res.mode === 'review' || res.mode === 'instant')) {
                this.setState({ approvalMode: res.mode });
            }
        } catch (_) { /* non-critical — default 'review' stays */ }
    }

    async saveApprovalMode(nextMode) {
        const prev = this.state.approvalMode;
        if (nextMode === prev) return;
        this.setState({ approvalMode: nextMode, approvalSaving: true, approvalSaveStatus: 'Saving…' });
        try {
            await fetchJson('/api/settings/brain-write-approval-mode', {
                method: 'PUT',
                body: { mode: nextMode },
            });
            this.setState({ approvalSaving: false, approvalSaveStatus: 'Saved' });
            // Auto-hide the status + close popover after a short grace so the
            // user sees confirmation land, then the menu gets out of the way.
            setTimeout(() => {
                if (this.state.approvalSaveStatus === 'Saved') {
                    this.setState({ approvalSaveStatus: '', approvalPopoverOpen: false });
                }
            }, 900);
        } catch (err) {
            console.error('[BRAIN_WRITE_MODE] save failed:', err.message);
            this.setState({
                approvalMode: prev,
                approvalSaving: false,
                approvalSaveStatus: 'Save failed — try again',
            });
        }
    }

    setState(patch) {
        Object.assign(this.state, patch);
        this.render();
    }

    async load() {
        // Phase 31H: 10s timeout → error state so a hung request doesn't leave
        // a permanent spinner. Retry via data-action="retry-load".
        const timeoutMs = 10000;
        const withTimeout = (p) => Promise.race([
            p,
            new Promise((_, rej) => setTimeout(() => rej(new Error("Couldn't load pending drafts.")), timeoutMs))
        ]);
        try {
            const [listRes, countsRes] = await withTimeout(Promise.all([
                fetchJson(`/api/agent-actions?status=${this.state.filter}&limit=100`),
                fetchJson('/api/agent-actions/counts'),
            ]));
            this.setState({
                loading: false,
                items: listRes.items || [],
                counts: countsRes || this.state.counts,
                selected: new Set(),
            });
        } catch (err) {
            this.setState({ loading: false, error: err.message });
        }
    }

    attachListeners() {
        this.el.addEventListener('click', async (e) => {
            const btn = e.target.closest('[data-action]');
            if (!btn) return;
            const action = btn.dataset.action;
            const id = btn.dataset.id;

            if (action === 'set-filter') {
                this.setState({ filter: btn.dataset.filter, selected: new Set() });
                await this.load();
            } else if (action === 'toggle-select') {
                const sel = new Set(this.state.selected);
                if (sel.has(id)) sel.delete(id); else sel.add(id);
                this.setState({ selected: sel });
            } else if (action === 'select-all') {
                this.setState({ selected: new Set(this.state.items.map(i => i.id)) });
            } else if (action === 'select-none') {
                this.setState({ selected: new Set() });
            } else if (action === 'approve') {
                await this.approve(id);
            } else if (action === 'reject') {
                await this.reject(id);
            } else if (action === 'edit-start') {
                const item = this.state.items.find(i => i.id === id);
                this.setState({ editing: id, editBuffer: item?.content || '' });
            } else if (action === 'edit-save') {
                await this.approve(id, this.state.editBuffer);
            } else if (action === 'edit-cancel') {
                this.setState({ editing: null, editBuffer: '' });
            } else if (action === 'bulk-approve') {
                await this.bulk('approve');
            } else if (action === 'bulk-reject') {
                await this.bulk('reject');
            } else if (action === 'group-approve' || action === 'group-reject') {
                // Pre-select every row in the group, then fire the same bulk flow.
                const ids = (btn.dataset.ids || '').split(',').filter(Boolean);
                if (ids.length === 0) return;
                const sel = new Set(this.state.selected);
                ids.forEach(i => sel.add(i));
                this.state.selected = sel;
                await this.bulk(action === 'group-approve' ? 'approve' : 'reject');
            } else if (action === 'toggle-import') {
                this.setState({ importOpen: !this.state.importOpen });
            } else if (action === 'import-submit') {
                await this.importPaste();
            } else if (action === 'set-view') {
                const mode = btn.dataset.view;
                try { localStorage.setItem('heist-inbox-view', mode); } catch (_) {}
                this.setState({ viewMode: mode });
            } else if (action === 'toggle-expand') {
                const ex = new Set(this.state.expanded);
                if (ex.has(id)) ex.delete(id); else ex.add(id);
                this.setState({ expanded: ex });
            } else if (action === 'delete') {
                await this.promptDelete(id);
            } else if (action === 'bulk-delete') {
                await this.promptBulkDelete();
            } else if (action === 'toggle-approval-popover') {
                // Stop propagation so the document-level click-outside
                // handler doesn't immediately see this click as an "outside"
                // tap. render() replaces innerHTML synchronously, detaching
                // the clicked button — by the time the event bubbles to
                // document, contains() returns false and the popover would
                // close itself right after opening.
                e.stopPropagation();
                this.setState({
                    approvalPopoverOpen: !this.state.approvalPopoverOpen,
                    approvalSaveStatus: '',
                });
            } else if (action === 'set-approval-mode') {
                e.stopPropagation();
                const nextMode = btn.value || btn.dataset.mode;
                if (nextMode === 'review' || nextMode === 'instant') {
                    this.saveApprovalMode(nextMode);
                }
            }
        });

        // Click-outside handler for the approval popover. Runs on document so
        // we catch taps/clicks that land outside `this.el` too.
        this._approvalOutsideHandler = (e) => {
            if (!this.state.approvalPopoverOpen) return;
            const root = this.el?.querySelector('[data-approval-root]');
            if (root && !root.contains(e.target)) {
                this.setState({ approvalPopoverOpen: false, approvalSaveStatus: '' });
            }
        };
        document.addEventListener('click', this._approvalOutsideHandler);
        this.el.addEventListener('input', (e) => {
            if (e.target.id === 'edit-textarea') this.state.editBuffer = e.target.value;
            if (e.target.id === 'reject-reason') this.state.rejectReason = e.target.value;
            if (e.target.id === 'reject-note') this.state.rejectNote = e.target.value;
            if (e.target.id === 'import-textarea') this.state.importText = e.target.value;
            if (e.target.id === 'import-source') this.state.importSource = e.target.value;
        });
    }

    async importPaste() {
        const raw = (this.state.importText || '').trim();
        if (!raw) { toast('error','Paste JSON first'); return; }
        let parsed;
        try { parsed = JSON.parse(raw); } catch (e) { toast('error','Not valid JSON'); return; }
        const items = Array.isArray(parsed) ? parsed : (Array.isArray(parsed.items) ? parsed.items : null);
        if (!items) { toast('error','Expected an array of drafts, or { items: [...] }'); return; }
        this.setState({ importBusy: true });
        try {
            const res = await fetchJson('/api/agent-import/drafts', {
                method: 'POST',
                body: { items, sourceLabel: this.state.importSource },
            });
            toast('success',`${res.successful}/${res.count} imported`);
            this.setState({ importBusy: false, importOpen: false, importText: '' });
            window.dispatchEvent(new CustomEvent('heist:agent-inbox-changed'));
            await this.load();
        } catch (err) {
            this.setState({ importBusy: false });
            toast('error',err.message || 'Import failed');
        }
    }

    async approve(id, editedContent) {
        try {
            const body = editedContent != null ? { edit: { content: editedContent } } : {};
            await fetchJson(`/api/agent-actions/${id}/approve`, { method: 'POST', body });
            toast('success','Approved');
            this.setState({ editing: null, editBuffer: '' });
            window.dispatchEvent(new CustomEvent('heist:agent-inbox-changed'));
            await this.load();
        } catch (err) {
            toast('error',err.message || 'Approve failed');
        }
    }

    async reject(id) {
        const ok = await confirmModal({
            title: 'Reject this draft?',
            message: 'Reason will be stored as a rule_dont so the agent learns from it.',
            confirmLabel: 'Reject',
            danger: true,
        });
        if (!ok) return;
        try {
            await fetchJson(`/api/agent-actions/${id}/reject`, {
                method: 'POST',
                body: { reason: this.state.rejectReason, note: this.state.rejectNote },
            });
            toast('success','Rejected');
            window.dispatchEvent(new CustomEvent('heist:agent-inbox-changed'));
            await this.load();
        } catch (err) {
            toast('error',err.message || 'Reject failed');
        }
    }

    async promptDelete(id) {
        const scope = await showDeleteScopeModal({ count: 1 });
        if (!scope) return;
        try {
            await fetchJson(`/api/agent-actions/${id}/${scope}`, { method: 'POST' });
            toast('success',scope === 'dismiss' ? 'Removed from inbox' : 'Content deleted');
            window.dispatchEvent(new CustomEvent('heist:agent-inbox-changed'));
            await this.load();
        } catch (err) {
            toast('error',err.message || 'Delete failed');
        }
    }

    async promptBulkDelete() {
        const ids = Array.from(this.state.selected);
        if (ids.length === 0) { toast('error','Nothing selected'); return; }
        const scope = await showDeleteScopeModal({ count: ids.length });
        if (!scope) return;
        try {
            const res = await fetchJson('/api/agent-actions/bulk', {
                method: 'POST',
                body: { ids, op: scope },
            });
            toast('success',`${res.successful}/${res.count} ${scope === 'dismiss' ? 'removed' : 'deleted'}`);
            window.dispatchEvent(new CustomEvent('heist:agent-inbox-changed'));
            await this.load();
        } catch (err) {
            toast('error',err.message || 'Bulk delete failed');
        }
    }

    async bulk(op) {
        const ids = Array.from(this.state.selected);
        if (ids.length === 0) { toast('error','Nothing selected'); return; }
        const confirmLabel = op === 'approve' ? `Approve ${ids.length}` : `Reject ${ids.length}`;
        const ok = await confirmModal({
            title: `${confirmLabel}?`,
            message: op === 'approve'
                ? 'These drafts will move from pending to draft (or scheduled if they have a scheduledAt).'
                : 'These drafts will be soft-deleted and the reason stored as a brand rule_dont.',
            confirmLabel,
            danger: op === 'reject',
        });
        if (!ok) return;
        try {
            const body = { ids, op };
            if (op === 'reject') { body.reason = this.state.rejectReason; body.note = this.state.rejectNote; }
            const res = await fetchJson('/api/agent-actions/bulk', { method: 'POST', body });
            toast('success',`${res.successful}/${res.count} succeeded`);
            window.dispatchEvent(new CustomEvent('heist:agent-inbox-changed'));
            await this.load();
        } catch (err) {
            toast('error',err.message || 'Bulk op failed');
        }
    }

    render() {
        if (!this.el) return;
        const s = this.state;
        // Reset per-render cache so renderItem() recomputes brand distinctness.
        this._distinctBrandCount = null;

        // Skip the page-header when embedded inside another shell (Settings tab).
        const headerHtml = this.embedded ? '' : renderPageHeader({
            title: 'Agent Inbox',
            subtitle: 'Post drafts written by Claude, ChatGPT, and your other connected AI apps land here. Review them, tweak what you want, then approve or toss.',
        });

        if (!isProTier()) {
            this.el.innerHTML = headerHtml + renderInboxUpgradeWall() + renderInboxStyles();
            return;
        }

        const filterDefs = [
            { key: 'pending', label: 'Pending', icon: ICON_CLOCK },
            { key: 'approved', label: 'Approved', icon: ICON_CHECK_CIRCLE },
            { key: 'rejected', label: 'Rejected', icon: ICON_X_CIRCLE },
            { key: 'executed', label: 'Executed', icon: ICON_ZAP },
            { key: 'all', label: 'All', icon: ICON_LAYERS },
        ];
        const filterTabs = filterDefs.map(f => {
            const count = s.counts[f.key] != null ? s.counts[f.key] : null;
            const isActive = s.filter === f.key;
            return `<button class="inbox-tab ${isActive ? 'is-active' : ''}" data-action="set-filter" data-filter="${f.key}">
                <span class="inbox-tab__icon">${f.icon}</span>
                <span class="inbox-tab__label">${f.label}</span>
                ${count != null && f.key !== 'all' ? `<span class="inbox-tab__count">${count}</span>` : ''}
            </button>`;
        }).join('');

        // Bulk bar — show whenever the current filter has any items. Users can delete from Approved/Executed tabs too.
        const showBulkBar = s.items.length > 0 && (s.filter === 'pending' || s.selected.size > 0);
        const bulkBar = (s.filter === 'pending' && s.items.length > 0) ? `
            <div class="inbox-bulk">
              <div class="inbox-bulk__left">
                <span class="inbox-bulk__count">${s.selected.size} / ${s.items.length} selected</span>
                <button class="inbox-btn inbox-btn--ghost inbox-btn--sm" data-action="select-all">Select all</button>
                <button class="inbox-btn inbox-btn--ghost inbox-btn--sm" data-action="select-none">Clear</button>
              </div>
              <div class="inbox-bulk__right">
                <label class="inbox-bulk__reject">
                  <span class="inbox-bulk__label">Reject reason</span>
                  <select id="reject-reason" class="inbox-select">
                    <option value="off-voice">off-voice</option>
                    <option value="factually-wrong">factually-wrong</option>
                    <option value="off-topic">off-topic</option>
                    <option value="already-said">already-said</option>
                    <option value="bad-grounding">bad-grounding</option>
                    <option value="other">other</option>
                  </select>
                </label>
                <input id="reject-note" class="inbox-input" placeholder="Note (optional)" />
                <button class="inbox-btn inbox-btn--approve" data-action="bulk-approve" ${s.selected.size === 0 ? 'disabled' : ''}>
                  Approve${s.selected.size ? ` (${s.selected.size})` : ''}
                </button>
                <button class="inbox-btn inbox-btn--reject" data-action="bulk-reject" ${s.selected.size === 0 ? 'disabled' : ''}>
                  Reject${s.selected.size ? ` (${s.selected.size})` : ''}
                </button>
                <button class="inbox-btn inbox-btn--danger-ghost" data-action="bulk-delete" ${s.selected.size === 0 ? 'disabled' : ''}>
                  ${ICON_TRASH} Delete${s.selected.size ? ` (${s.selected.size})` : ''}
                </button>
              </div>
            </div>
        ` : '';

        // Non-pending filters (approved/rejected/executed/all) — show a slim selection bar when the user selects items, so they can bulk-delete from any tab.
        const nonPendingBulkBar = (s.filter !== 'pending' && s.selected.size > 0) ? `
            <div class="inbox-bulk inbox-bulk--slim">
              <div class="inbox-bulk__left">
                <span class="inbox-bulk__count">${s.selected.size} selected</span>
                <button class="inbox-btn inbox-btn--ghost inbox-btn--sm" data-action="select-none">Clear</button>
              </div>
              <div class="inbox-bulk__right">
                <button class="inbox-btn inbox-btn--danger-ghost" data-action="bulk-delete">
                  ${ICON_TRASH} Delete (${s.selected.size})
                </button>
              </div>
            </div>
        ` : '';

        const emptyStates = {
            pending: {
                title: 'Quiet for now.',
                sub: "Connect Claude or ChatGPT under Settings → Connectors and anything they draft will show up here for your final say.",
                cta: '<a class="inbox-btn inbox-btn--ghost" href="#connectors">Manage connectors →</a>'
            },
            approved: { title: 'Nothing sitting in this bucket.', sub: "Approved drafts move straight to your Library — an empty list means you're all caught up.", cta: '' },
            rejected: { title: 'Nothing rejected.', sub: 'Rejections live here with the reason attached, so you can see what got screened out and why.', cta: '' },
            executed: { title: 'No auto-published drafts yet.', sub: 'Posts that skip review (because you gave an agent that permission) land here as an audit trail.', cta: '' },
            all: { title: 'Nothing here yet.', sub: "Once a connector is active, everything it drafts — pending, approved, rejected, or executed — threads through this inbox.", cta: '<a class="inbox-btn inbox-btn--ghost" href="#connectors">Connect your first AI →</a>' },
        };
        const emptyDef = emptyStates[s.filter] || emptyStates.pending;
        const emptyIcons = {
            pending: ICON_SPARKLE_LG,
            approved: ICON_CHECK_LG,
            rejected: ICON_X_LG,
            executed: ICON_ZAP_LG,
            all: ICON_INBOX_LG,
        };
        const empty = (s.items.length === 0 && !s.loading && !s.error) ? `
            <div class="inbox-empty">
              <div class="inbox-empty__halo" aria-hidden="true"></div>
              <div class="inbox-empty__icon">${emptyIcons[s.filter] || ICON_INBOX_LG}</div>
              <div class="inbox-empty__title">${emptyDef.title}</div>
              <div class="inbox-empty__sub">${emptyDef.sub}</div>
              ${emptyDef.cta ? `<div class="inbox-empty__cta">${emptyDef.cta}</div>` : ''}
            </div>
        ` : '';

        // Group Pending rows by source when any one source has ≥ GROUPING_THRESHOLD items.
        // Keeps small inboxes ungrouped and avoids double-headers in list-view where
        // rows are already compact.
        const useGrouping = s.filter === 'pending' && s.viewMode === 'card' && s.items.length >= GROUPING_THRESHOLD;
        let itemsHtml;
        if (useGrouping) {
            const groups = groupItemsBySource(s.items);
            const needsGroup = groups.some(g => g.rows.length >= GROUPING_THRESHOLD);
            if (needsGroup) {
                itemsHtml = groups.map(g => {
                    const theme = deriveSourceTheme(g.key);
                    const ids = g.rows.map(r => r.id).join(',');
                    const headerHtml = g.rows.length >= GROUPING_THRESHOLD ? `
                        <div class="inbox-group-head">
                          <div class="inbox-group-head__label">
                            <span class="inbox-source__dot" style="background:${theme.dot}"></span>
                            <span class="inbox-group-head__count">${g.rows.length}</span>
                            <span class="inbox-group-head__name">${escapeHtml(theme.label)}</span>
                            <span class="inbox-group-head__hint">drafts</span>
                          </div>
                          <div class="inbox-group-head__actions">
                            <button class="inbox-btn inbox-btn--sm inbox-btn--ghost" data-action="group-approve" data-ids="${ids}">Approve all</button>
                            <button class="inbox-btn inbox-btn--sm inbox-btn--danger-ghost" data-action="group-reject" data-ids="${ids}">Reject all</button>
                          </div>
                        </div>` : '';
                    return headerHtml + g.rows.map(r => this.renderItem(r)).join('');
                }).join('');
            } else {
                itemsHtml = s.items.map(item => this.renderItem(item)).join('');
            }
        } else {
            itemsHtml = s.items.map(item => this.renderItem(item)).join('');
        }

        const importPanel = s.importOpen ? `
            <div class="inbox-import">
              <div class="inbox-import__head">
                <div>
                  <h3>Paste in drafts from Gemini or ChatGPT</h3>
                  <p class="inbox-dim">If your AI can't connect directly, you can still bring its drafts in the old-fashioned way. Copy the text it wrote and paste it below — it'll show up like any other draft for you to review.</p>
                </div>
                <button class="inbox-btn inbox-btn--ghost inbox-btn--sm" data-action="toggle-import">Close</button>
              </div>
              <label class="inbox-field inbox-field--inline">
                <span class="inbox-field__label">Which AI wrote these?</span>
                <input id="import-source" class="inbox-input" placeholder="e.g. Gemini, ChatGPT" value="${escapeHtml(s.importSource)}" />
              </label>
              <textarea id="import-textarea" class="inbox-code-input" placeholder='Paste the draft text here. If your AI gave you multiple drafts in a structured format (JSON), that works too — Heist will figure it out.'>${escapeHtml(s.importText)}</textarea>
              <div class="inbox-import__actions">
                <button class="inbox-btn inbox-btn--primary" data-action="import-submit" ${s.importBusy ? 'disabled' : ''}>${s.importBusy ? 'Importing…' : 'Add to inbox'}</button>
                <button class="inbox-btn inbox-btn--ghost" data-action="toggle-import">Cancel</button>
              </div>
            </div>
        ` : '';

        const loadingHtml = s.loading
            ? `<div class="inbox-skeleton"></div><div class="inbox-skeleton"></div><div class="inbox-skeleton"></div>`
            : '';
        const errorHtml = s.error ? `<div class="inbox-error">${escapeHtml(s.error)}</div>` : '';

        this.el.innerHTML = `
          ${headerHtml}
          <div class="inbox-page">
            <div class="inbox-toolbar">
              <div class="inbox-tabs">${filterTabs}</div>
              <div class="inbox-toolbar__spacer"></div>
              <div class="inbox-view-toggle" role="group" aria-label="Change view">
                <button class="inbox-view-btn ${s.viewMode === 'list' ? 'is-active' : ''}" data-action="set-view" data-view="list" title="List view" aria-label="List view">${ICON_LIST}</button>
                <button class="inbox-view-btn ${s.viewMode === 'card' ? 'is-active' : ''}" data-action="set-view" data-view="card" title="Card view" aria-label="Card view">${ICON_GRID}</button>
              </div>
              <button class="inbox-btn inbox-btn--ghost inbox-btn--sm" data-action="toggle-import">
                ${s.importOpen ? 'Close' : `${ICON_PASTE} Paste from another AI`}
              </button>
              ${this.renderApprovalModeControl()}
            </div>
            ${importPanel}
            ${bulkBar}
            ${nonPendingBulkBar}
            ${loadingHtml}
            ${errorHtml}
            ${empty}
            <div class="inbox-items inbox-items--${s.viewMode}">${itemsHtml}</div>
          </div>
          ${renderInboxStyles()}
        `;
    }

    renderApprovalModeControl() {
        const s = this.state;
        const isInstant = s.approvalMode === 'instant';
        const modeLabel = isInstant ? 'Instant' : 'Review first';
        const open = !!s.approvalPopoverOpen;
        const status = s.approvalSaveStatus || '';
        return `
            <div class="inbox-approval" data-approval-root>
              <button
                class="inbox-btn inbox-btn--ghost inbox-btn--sm inbox-approval__btn ${open ? 'is-open' : ''}"
                data-action="toggle-approval-popover"
                title="How AI Brain writes are handled"
                aria-haspopup="true"
                aria-expanded="${open}">
                <span class="inbox-approval__dot inbox-approval__dot--${isInstant ? 'instant' : 'review'}" aria-hidden="true"></span>
                <span class="inbox-approval__label">AI writes: ${modeLabel}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.55;"><polyline points="6 9 12 15 18 9"></polyline></svg>
              </button>
              ${open ? `
                <div class="inbox-approval__popover" role="menu" onclick="event.stopPropagation()">
                  <div class="inbox-approval__header">
                    <span class="inbox-approval__title">How AI changes to my Brain land</span>
                    <span class="inbox-approval__hint">Synced with Settings → AI. Takes effect on the next MCP write.</span>
                  </div>
                  <label class="inbox-approval__option ${s.approvalMode === 'review' ? 'is-active' : ''}">
                    <input type="radio" name="inbox-approval-mode" value="review" ${s.approvalMode === 'review' ? 'checked' : ''} data-action="set-approval-mode" />
                    <span class="inbox-approval__option-body">
                      <span class="inbox-approval__option-title">Review first <span class="inbox-approval__option-tag">recommended</span></span>
                      <span class="inbox-approval__option-desc">Every AI write waits here until you approve. Safest while you're still learning what your agents propose.</span>
                    </span>
                  </label>
                  <label class="inbox-approval__option ${s.approvalMode === 'instant' ? 'is-active' : ''}">
                    <input type="radio" name="inbox-approval-mode" value="instant" ${s.approvalMode === 'instant' ? 'checked' : ''} data-action="set-approval-mode" />
                    <span class="inbox-approval__option-body">
                      <span class="inbox-approval__option-title">Apply instantly</span>
                      <span class="inbox-approval__option-desc">AI writes land live immediately. Every change still records an audit row so you can review or undo from here.</span>
                    </span>
                  </label>
                  ${status ? `<div class="inbox-approval__status inbox-approval__status--${status === 'Saved' ? 'ok' : (status === 'Saving…' ? 'pending' : 'err')}">${status}</div>` : ''}
                </div>
              ` : ''}
            </div>
        `;
    }

    renderItem(item) {
        const s = this.state;
        const editing = s.editing === item.id;
        const isExpanded = s.expanded.has(item.id) || editing;
        const rawLabel = item.clientLabel || 'unknown';
        const chipTheme = deriveSourceTheme(rawLabel);
        const highRisk = item.flags && item.flags.highRisk;
        // Phase D1: Brain-write actions (add/update/remove fact, update profile,
        // rate post) land here alongside draft actions. Render them with a
        // distinct preview + expanded metadata block instead of the content card.
        const brainWrite = formatBrainWriteSummary(item);

        const sourceChip = `<span class="inbox-source" style="--src-bg:${chipTheme.bg};--src-color:${chipTheme.color};--src-color-dark:${chipTheme.colorDark || chipTheme.color};--src-border:${chipTheme.border}">
            <span class="inbox-source__dot" style="background:${chipTheme.dot}"></span>${escapeHtml(chipTheme.label)}
        </span>`;

        const violationChips = (item.flags && item.flags.rulesDontViolations || []).map(v =>
            `<span class="inbox-violation">⚠ ${escapeHtml(v.rule || v.match || 'rule broken')}</span>`
        ).join('');

        const groundedPills = item.groundedOn ? renderGroundedOn(item.groundedOn) : '';

        // For Brain-write actions the body is the structured summary block;
        // the regular content textarea/preview is only used for draft actions.
        const contentBlock = brainWrite
            ? brainWrite.detailHtml
            : (editing
                ? `<textarea id="edit-textarea" class="inbox-edit-textarea">${escapeHtml(s.editBuffer)}</textarea>`
                : `<div class="inbox-content">${escapeHtml(item.content || '(no content)')}</div>`);

        const deleteBtn = `<button class="inbox-btn inbox-btn--icon-only inbox-btn--danger-ghost" data-action="delete" data-id="${item.id}" title="Delete…" aria-label="Delete">${ICON_TRASH}</button>`;

        // Brain-write actions skip the inline Edit button — they don't share
        // the content textarea flow. Users can still reject and have the
        // agent propose again with refined wording.
        const actions = item.status === 'pending'
            ? (editing
                ? `<button class="inbox-btn inbox-btn--approve" data-action="edit-save" data-id="${item.id}">Save & Approve</button>
                   <button class="inbox-btn inbox-btn--ghost" data-action="edit-cancel">Cancel</button>`
                : `${deleteBtn}
                   <span class="inbox-spacer"></span>
                   ${brainWrite ? '' : `<button class="inbox-btn inbox-btn--ghost" data-action="edit-start" data-id="${item.id}">Edit</button>`}
                   <button class="inbox-btn inbox-btn--reject" data-action="reject" data-id="${item.id}">Reject</button>
                   <button class="inbox-btn inbox-btn--approve" data-action="approve" data-id="${item.id}">Approve</button>`
            )
            : `${deleteBtn}
               <span class="inbox-decided">
                  <span class="inbox-status inbox-status--${item.status}">${item.status}</span>
                  ${item.reason ? `<span class="inbox-dim">· ${escapeHtml(item.reason)}</span>` : ''}
                  ${item.decidedAt ? `<span class="inbox-dim">· ${new Date(item.decidedAt).toLocaleString()}</span>` : ''}
               </span>`;

        const platformLabel = item.platform ? escapeHtml(String(item.platform).toUpperCase()) : '—';
        // Derive distinct brand count client-side from items so single-brand
        // workspaces don't render a redundant "Heist" chip on every row.
        if (!this._distinctBrandCount) {
            this._distinctBrandCount = new Set(s.items.map(it => it.brandId || it.brandName).filter(Boolean)).size;
        }
        const showBrandChip = this._distinctBrandCount > 1;
        const brandLabel = escapeHtml(item.brandName || item.brandId || '—');

        // Preview: strip HTML so rich drafts (e.g. MCP clients sending <div
        // class="heist-tldr">…) render as readable plain text. When the linked
        // content_items row is missing (a known mid-write failure mode where
        // the agent_action landed but the content insert didn't), fall back to
        // metadata.title so the row at least identifies itself.
        const plain = stripHtmlForPreview(item.content);
        const titleFallback = item.title || (item.metadata && item.metadata.title) || '';
        // Brain-write actions get their own preview derived from metadata;
        // draft actions fall back to the content/title chain as before.
        const rawPreview = brainWrite
            ? brainWrite.preview
            : (plain || titleFallback || '(draft content not found — safe to delete)');
        const PREVIEW_CAP = 160;
        const preview = rawPreview.length > PREVIEW_CAP ? (rawPreview.slice(0, PREVIEW_CAP).trimEnd() + '…') : rawPreview;

        const schedChip = formatScheduledChip(item.scheduledAt);
        const voiceChip = formatVoiceChip(item.score);
        const violationCount = (item.flags && item.flags.rulesDontViolations || []).length;

        // Checkbox on every item so the user can bulk-select + bulk-delete even on approved/rejected/executed rows.
        const selectBox = `<label class="inbox-check inbox-check--inline" onclick="event.stopPropagation()">
             <input type="checkbox" data-action="toggle-select" data-id="${item.id}" ${s.selected.has(item.id) ? 'checked' : ''} />
           </label>`;

        // Brain-write chips: show the Brain-action kind badge instead of the
        // platform/voice chips (which only apply to drafts).
        const line2Chips = brainWrite
            ? [
                `<span class="inbox-chip inbox-chip--brain-write" title="Proposed Brain change" style="background:rgba(139,92,246,0.14);color:#A78BFA;border-color:rgba(139,92,246,0.35);">${brainWrite.kindLabel}</span>`,
                showBrandChip ? `<span class="inbox-chip inbox-chip--brand" title="Brand">${brandLabel}</span>` : '',
            ].filter(Boolean).join('')
            : [
                `<span class="inbox-chip inbox-chip--platform" title="Posting target">${platformLabel}</span>`,
                showBrandChip ? `<span class="inbox-chip inbox-chip--brand" title="Brand">${brandLabel}</span>` : '',
                schedChip ? `<span class="inbox-chip inbox-chip--sched inbox-chip--sched--${schedChip.variant}" title="${escapeHtml(schedChip.title)}"><span class="inbox-chip__dot" style="background:${schedChip.dot}"></span>${schedChip.prefix} ${escapeHtml(schedChip.date)}</span>` : '',
                voiceChip ? `<span class="inbox-chip inbox-chip--voice inbox-chip--voice--${voiceChip.variant}" title="${escapeHtml(voiceChip.title)}">${voiceChip.label}</span>` : '',
                violationCount && item.status === 'pending' ? `<span class="inbox-chip inbox-chip--risk" title="Rules the draft broke — open to review">⚠ ${violationCount} rule${violationCount === 1 ? '' : 's'} broken</span>` : '',
            ].filter(Boolean).join('');

        return `
          <article class="inbox-card ${highRisk ? 'is-high-risk' : ''} is-${item.status} ${isExpanded ? 'is-expanded' : 'is-collapsed'}">
            <header class="inbox-card__head" data-action="toggle-expand" data-id="${item.id}" role="button" tabindex="0" aria-expanded="${isExpanded}">
              <div class="inbox-card__line1">
                ${selectBox}
                ${sourceChip}
                <span class="inbox-card__preview" title="${escapeHtml(rawPreview.slice(0, 500))}">${escapeHtml(preview)}</span>
                <time class="inbox-dim inbox-time" title="${item.requestedAt ? escapeHtml(new Date(item.requestedAt).toLocaleString()) : ''}">${item.requestedAt ? timeAgo(item.requestedAt) : ''}</time>
                <span class="inbox-card__chevron" aria-hidden="true">${ICON_CHEVRON}</span>
              </div>
              <div class="inbox-card__line2">
                ${line2Chips}
              </div>
            </header>
            <div class="inbox-card__body">
              ${violationChips ? `<div class="inbox-violations">${violationChips}</div>` : ''}
              ${contentBlock}
              ${groundedPills ? `<div class="inbox-grounded"><span class="inbox-field__label">Grounded on</span><div>${groundedPills}</div></div>` : ''}
              <footer class="inbox-card__foot">${actions}</footer>
            </div>
          </article>
        `;
    }

    unmount() {
        // Detach the document-level click-outside listener so it doesn't leak
        // across page navigations.
        if (this._approvalOutsideHandler) {
            document.removeEventListener('click', this._approvalOutsideHandler);
            this._approvalOutsideHandler = null;
        }
        if (this.el) this.el.innerHTML = '';
    }
}

// ─── source chip theming ─────────────────────────────────────────────────
// Each preset carries two text colors: `color` for light mode (dark ink on a
// tinted light background) and `colorDark` for dark mode (pastel ink on a
// dimmed dark background). See the `html.dark .inbox-source` rule below.
const SOURCE_PRESETS = {
    'agent:claude':        { bg: 'rgba(253,227,212,0.55)', color: '#7a3415', colorDark: '#FDBA74', border: 'rgba(214,113,52,0.28)',  dot: '#D97706', label: 'Claude' },
    'agent:claude_desktop':{ bg: 'rgba(253,227,212,0.55)', color: '#7a3415', colorDark: '#FDBA74', border: 'rgba(214,113,52,0.28)',  dot: '#D97706', label: 'Claude Desktop' },
    'agent:chatgpt':       { bg: 'rgba(212,245,228,0.55)', color: '#0f5132', colorDark: '#6EE7B7', border: 'rgba(34,139,85,0.28)',   dot: '#10B981', label: 'ChatGPT' },
    'agent:copilot':       { bg: 'rgba(227,228,253,0.55)', color: '#1e2170', colorDark: '#A5B4FC', border: 'rgba(84,90,212,0.28)',   dot: '#4F46E5', label: 'Copilot' },
    'agent:cursor':        { bg: 'rgba(212,228,253,0.55)', color: '#0f3a7a', colorDark: '#93C5FD', border: 'rgba(52,113,214,0.28)',  dot: '#3B82F6', label: 'Cursor' },
    'agent:local_stdio':   { bg: 'rgba(244,227,253,0.55)', color: '#5a1d78', colorDark: '#D8B4FE', border: 'rgba(144,52,214,0.28)',  dot: '#A855F7', label: 'Local stdio' },
    'agent:gemini_paste':  { bg: 'rgba(253,243,212,0.55)', color: '#7a5415', colorDark: '#FDE68A', border: 'rgba(214,167,52,0.28)',  dot: '#EAB308', label: 'Gemini (paste)' },
    'api_key':             { bg: 'var(--surface-2)',       color: 'var(--text-2)', colorDark: 'var(--text)', border: 'var(--border-subtle)', dot: '#64748B', label: 'API key' },
    'heist_ui':            { bg: 'var(--bg-accent-1)',     color: 'var(--primary)', colorDark: '#C4B5FD', border: 'rgba(139,92,246,0.25)', dot: '#8B5CF6', label: 'Heist UI' },
};

function deriveSourceTheme(raw) {
    if (SOURCE_PRESETS[raw]) return SOURCE_PRESETS[raw];
    // Humanize: strip either `agent:` or `oauth:` prefix, swap underscores → spaces
    const suffix = String(raw || '').replace(/^(agent|oauth):/i, '').replace(/_/g, ' ').trim();
    const probe = suffix.toLowerCase();

    // Regex fallback: MCP clients self-report a client_name during DCR.
    // Common hosts get their brand treatment; anything else renders as a
    // subtle generic chip with the name verbatim.
    if (/claude\s*desktop|anthropic\s*desktop/.test(probe)) return { ...SOURCE_PRESETS['agent:claude_desktop'], label: 'Claude Desktop' };
    if (/claude\.?ai|\bclaude\b/.test(probe))                return { ...SOURCE_PRESETS['agent:claude'],         label: /claude\.?ai/.test(probe) ? 'Claude.ai' : (suffix || 'Claude') };
    if (/chatgpt|open\s*ai/.test(probe))                     return { ...SOURCE_PRESETS['agent:chatgpt'],        label: 'ChatGPT' };
    if (/\bcursor\b/.test(probe))                            return { ...SOURCE_PRESETS['agent:cursor'],         label: 'Cursor' };
    if (/copilot|\bgithub\b/.test(probe))                    return { ...SOURCE_PRESETS['agent:copilot'],        label: /github/.test(probe) ? 'GitHub Copilot' : 'Copilot' };
    if (/gemini|\bbard\b/.test(probe))                       return { ...SOURCE_PRESETS['agent:gemini_paste'],   label: 'Gemini' };

    // Unknown: generic chip, show whatever the name actually was
    const genericLabel = suffix === 'unknown client' ? 'Unknown client' : (suffix || 'Unknown');
    return { bg: 'var(--surface-2)', color: 'var(--text-2)', colorDark: 'var(--text)', border: 'var(--border-subtle)', dot: '#64748B', label: genericLabel };
}

// Strip HTML/style blocks so the preview reads as plain text instead of
// `<div class="heist-tldr" styl…` when an MCP client sends rich drafts.
function stripHtmlForPreview(raw) {
    return String(raw || '')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&quot;/gi, '"')
        .replace(/\s+/g, ' ')
        .trim();
}

// Turn an ISO scheduledAt into a labeled chip config (Scheduled / Overdue).
// Returns null when there's nothing to show so callers can omit the chip.
function formatScheduledChip(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const now = Date.now();
    const isPast = d.getTime() < now;
    const short = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return {
        prefix: isPast ? 'Overdue' : 'Scheduled',
        date: short,
        variant: isPast ? 'overdue' : 'future',
        dot: isPast ? '#F59E0B' : '#10B981',
        title: d.toLocaleString(),
    };
}

// Phase D1: summarize a Brain-write agent_action for the inbox card.
// Returns null for non-Brain-write actions so renderItem() can fall back
// to the existing draft-card path.
const BRAIN_WRITE_ACTION_TYPES_UI = new Set([
    'add_memory_fact', 'update_memory_fact', 'remove_memory_fact',
    'update_brand_profile', 'rate_post_performance',
]);
const BRAIN_KIND_LABELS = {
    add_memory_fact: 'New Brain fact',
    update_memory_fact: 'Edit Brain fact',
    remove_memory_fact: 'Remove Brain fact',
    update_brand_profile: 'Brand profile update',
    rate_post_performance: 'Post rating',
};
function formatBrainWriteSummary(item) {
    if (!item || !BRAIN_WRITE_ACTION_TYPES_UI.has(item.actionType)) return null;
    const m = item.metadata || {};
    const kindLabel = BRAIN_KIND_LABELS[item.actionType] || item.actionType;
    const rowHtml = (label, value, klass = '') =>
        value != null && value !== ''
            ? `<div class="bw-row ${klass}"><span class="bw-row__label">${escapeHtml(label)}</span><span class="bw-row__value">${escapeHtml(String(value))}</span></div>`
            : '';

    if (item.actionType === 'add_memory_fact') {
        const content = m.content || '';
        return {
            kindLabel,
            preview: `${kindLabel}: "${content.slice(0, 120)}${content.length > 120 ? '…' : ''}"`,
            detailHtml: `
                <div class="inbox-brainwrite">
                    <div class="bw-proposal">${escapeHtml(content)}</div>
                    ${rowHtml('Type', m.factType)}
                    ${rowHtml('Source', m.source, 'bw-row--source')}
                </div>
            `,
        };
    }

    if (item.actionType === 'update_memory_fact') {
        const before = m.oldContent || '(empty)';
        const after = m.newContent || '(empty — only type changing)';
        return {
            kindLabel,
            preview: `${kindLabel}: "${String(after).slice(0, 120)}"`,
            detailHtml: `
                <div class="inbox-brainwrite">
                    <div class="bw-diff">
                        <div class="bw-diff__col"><div class="bw-diff__label">Before</div><div class="bw-diff__body">${escapeHtml(before)}</div></div>
                        <div class="bw-diff__col"><div class="bw-diff__label">After</div><div class="bw-diff__body">${escapeHtml(after)}</div></div>
                    </div>
                    ${m.oldFactType || m.newFactType ? rowHtml('Type', `${m.oldFactType || '(unchanged)'} → ${m.newFactType || '(unchanged)'}`) : ''}
                    ${rowHtml('Reason', m.reason, 'bw-row--source')}
                </div>
            `,
        };
    }

    if (item.actionType === 'remove_memory_fact') {
        const content = m.removedContent || '';
        return {
            kindLabel,
            preview: `${kindLabel}: "${content.slice(0, 120)}${content.length > 120 ? '…' : ''}"`,
            detailHtml: `
                <div class="inbox-brainwrite">
                    <div class="bw-proposal bw-proposal--removed">${escapeHtml(content)}</div>
                    ${rowHtml('Reason', m.reason, 'bw-row--source')}
                </div>
            `,
        };
    }

    if (item.actionType === 'update_brand_profile') {
        const before = m.before || {};
        const after = m.after || {};
        const fields = m.patchFields || Object.keys(after);
        const fieldHtml = fields.map(f => `
            <div class="bw-diff">
                <div class="bw-diff__col"><div class="bw-diff__label">${escapeHtml(f)} · before</div><div class="bw-diff__body">${escapeHtml(before[f] || '(empty)')}</div></div>
                <div class="bw-diff__col"><div class="bw-diff__label">${escapeHtml(f)} · after</div><div class="bw-diff__body">${escapeHtml(after[f] || '')}</div></div>
            </div>
        `).join('');
        const firstField = fields[0] || 'profile';
        return {
            kindLabel,
            preview: `${kindLabel}: ${fields.length} field${fields.length === 1 ? '' : 's'} (${firstField}${fields.length > 1 ? '…' : ''})`,
            detailHtml: `
                <div class="inbox-brainwrite">
                    ${fieldHtml}
                    ${rowHtml('Reason', m.changeReason, 'bw-row--source')}
                </div>
            `,
        };
    }

    if (item.actionType === 'rate_post_performance') {
        const r = m.rating || 'neutral';
        const emoji = r === 'high' ? '📈' : (r === 'low' ? '📉' : '➡️');
        return {
            kindLabel,
            preview: `${kindLabel}: ${emoji} ${r.toUpperCase()}`,
            detailHtml: `
                <div class="inbox-brainwrite">
                    <div class="bw-rating">${emoji} <strong>${escapeHtml(r.toUpperCase())}</strong></div>
                    ${m.contentPreview ? `<div class="bw-post-preview">"${escapeHtml(m.contentPreview)}${String(m.contentPreview).length >= 120 ? '…' : ''}"</div>` : ''}
                    ${rowHtml('Platform', m.platform)}
                    ${rowHtml('Rationale', m.rationale, 'bw-row--source')}
                </div>
            `,
        };
    }

    return null;
}

// Voice-score colorway — green 80+, amber 60-79, red <60.
function formatVoiceChip(score) {
    if (score == null || score === '') return null;
    const n = Number(score);
    if (!Number.isFinite(n) || n <= 0) return null; // 0 = not scored yet, don't render noise
    let variant = 'low';
    if (n >= 80) variant = 'high';
    else if (n >= 60) variant = 'mid';
    return { label: `Voice ${Math.round(n)}`, variant, title: 'Brand-voice match score — calculated from how closely the draft mirrors your top-scoring posts.' };
}

// Group Pending items by clientLabel when N ≥ 4 share a source — render a
// sticky group header with Approve-all / Reject-all. Called only when the
// Pending tab is active AND viewMode is 'card' (skipped in list-view so rows
// stay dense).
const GROUPING_THRESHOLD = 4;
function groupItemsBySource(items) {
    const groups = new Map();
    for (const it of items) {
        const key = it.clientLabel || 'unknown';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(it);
    }
    return Array.from(groups.entries()).map(([key, rows]) => ({ key, rows }));
}

function timeAgo(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diff = Date.now() - d.getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return m + 'm ago';
    const h = Math.floor(m / 60);
    if (h < 24) return h + 'h ago';
    const dy = Math.floor(h / 24);
    if (dy < 7) return dy + 'd ago';
    return d.toLocaleDateString();
}

/**
 * Two-scope delete modal. Resolves with:
 *   'dismiss' — remove from inbox only (content stays in Library)
 *   'purge'   — delete everywhere (unschedules + soft-deletes content)
 *   null      — user cancelled
 */
function showDeleteScopeModal({ count = 1 } = {}) {
    return new Promise(resolve => {
        const overlay = document.createElement('div');
        overlay.className = 'inbox-del-overlay';
        overlay.innerHTML = `
          <div class="inbox-del-modal" role="dialog" aria-modal="true">
            <h3 class="inbox-del-title">Delete ${count === 1 ? 'this item' : `${count} items`}</h3>
            <p class="inbox-del-sub">Pick what to delete. Removing from the inbox only keeps the content in your Library — useful if you've already reviewed it.</p>
            <div class="inbox-del-options">
              <button class="inbox-del-option" data-scope="dismiss">
                <div class="inbox-del-option__title">Remove from inbox only</div>
                <div class="inbox-del-option__sub">Clears the audit-trail row. Content stays in Library and any scheduled post still goes out.</div>
              </button>
              <button class="inbox-del-option inbox-del-option--danger" data-scope="purge">
                <div class="inbox-del-option__title">Delete content permanently</div>
                <div class="inbox-del-option__sub">Removes from the inbox AND deletes the linked content. Cancels any scheduled posts.</div>
              </button>
            </div>
            <div class="inbox-del-footer">
              <button class="inbox-btn inbox-btn--ghost" data-scope="">Cancel</button>
            </div>
          </div>`;
        document.body.appendChild(overlay);

        const finish = (scope) => {
            overlay.remove();
            resolve(scope || null);
        };
        overlay.addEventListener('click', e => {
            const btn = e.target.closest('[data-scope]');
            if (btn) return finish(btn.dataset.scope || null);
            if (e.target === overlay) finish(null);
        });
        document.addEventListener('keydown', function esc(ev) {
            if (ev.key === 'Escape') { document.removeEventListener('keydown', esc); finish(null); }
        });
    });
}

function renderInboxUpgradeWall() {
    return `
      <div class="inbox-page">
        <div class="conn-upgrade">
          <div class="conn-upgrade__halo" aria-hidden="true"></div>
          <span class="conn-upgrade__eyebrow">Pro feature</span>
          <h2 class="conn-upgrade__title">One inbox for every AI draft.</h2>
          <p class="conn-upgrade__lede">
            When Claude, ChatGPT, or any other connected AI writes post drafts, they all land here — labeled so you know who wrote what, and waiting for your final word.
          </p>
          <ul class="conn-upgrade__list">
            <li><span class="conn-upgrade__bullet"></span> See drafts from every AI in one place</li>
            <li><span class="conn-upgrade__bullet"></span> Approve, tweak, or reject — one at a time or in bulk</li>
            <li><span class="conn-upgrade__bullet"></span> Heist flags anything that breaks your brand rules automatically</li>
            <li><span class="conn-upgrade__bullet"></span> The more you review, the smarter your AIs get at matching your voice</li>
          </ul>
          <a href="#upgrade" class="conn-btn conn-btn--primary conn-btn--lg">Upgrade to Pro</a>
          <p class="conn-upgrade__footnote">Included on Pro, Business, Founder, and Agency plans.</p>
        </div>
      </div>`;
}

function renderGroundedOn(g) {
    const all = [];
    if (Array.isArray(g.facts)) g.facts.forEach(id => all.push({ k: 'fact', v: id }));
    if (Array.isArray(g.documents)) g.documents.forEach(id => all.push({ k: 'doc', v: id }));
    if (Array.isArray(g.reference_posts)) g.reference_posts.forEach(id => all.push({ k: 'ref', v: id }));
    if (Array.isArray(g.rules_applied)) g.rules_applied.forEach(id => all.push({ k: 'rule', v: id }));
    if (all.length === 0) return '<span class="inbox-grounded__empty">— none cited</span>';
    return all.map(p => `<span class="inbox-grounded__pill"><span class="inbox-grounded__kind">${p.k}</span>${escapeHtml(String(p.v).slice(0,8))}</span>`).join('');
}

function renderInboxStyles() {
    return `<style>
      .inbox-page { max-width: 1040px; margin: 0 auto; padding: 24px; display: flex; flex-direction: column; gap: 18px; }
      .inbox-page code, .inbox-page pre { font-family: var(--font-mono, 'JetBrains Mono', monospace); }
      .inbox-dim { color: var(--text-muted); font-size: 12.5px; }

      /* Toolbar + tabs */
      .inbox-toolbar {
        display: flex; align-items: center; gap: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--border-subtle);
      }
      .inbox-toolbar__spacer { flex: 1; }
      .inbox-tabs { display: flex; gap: 4px; flex-wrap: wrap; }
      .inbox-tab {
        display: inline-flex; align-items: center; gap: 7px;
        padding: 7px 13px; border-radius: 999px;
        background: transparent; border: 1px solid transparent;
        font-size: 13px; font-weight: 500; color: var(--text-2);
        cursor: pointer; font-family: inherit;
        transition: all .15s ease;
      }
      .inbox-tab:hover { background: var(--surface-2); color: var(--text); }
      .inbox-tab.is-active {
        background: var(--bg-accent-1); color: var(--primary);
        border-color: rgba(139,92,246,0.25);
      }
      .inbox-tab__icon {
        display: inline-flex; align-items: center;
        opacity: 0.8;
      }
      .inbox-tab.is-active .inbox-tab__icon { opacity: 1; }
      .inbox-tab__icon svg { display: block; }
      .inbox-tab__count {
        display: inline-grid; place-items: center; min-width: 20px; height: 18px;
        padding: 0 6px; font-size: 10.5px; font-family: var(--font-mono); font-weight: 600;
        border-radius: 999px;
        background: var(--surface-2); color: var(--text-muted);
      }
      .inbox-tab.is-active .inbox-tab__count {
        background: var(--primary); color: #fff;
      }

      /* Bulk action bar */
      .inbox-bulk {
        display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
        padding: 10px 14px; border-radius: 10px;
        background: linear-gradient(135deg, var(--bg-accent-1), transparent);
        border: 1px solid rgba(139,92,246,0.18);
      }
      .inbox-bulk__left, .inbox-bulk__right { display: flex; gap: 8px; align-items: center; }
      .inbox-bulk__right { flex: 1; flex-wrap: wrap; justify-content: flex-end; }
      .inbox-bulk__count { font-family: var(--font-mono); font-size: 12px; color: var(--text); }
      .inbox-bulk__reject { display: inline-flex; gap: 8px; align-items: center; }
      .inbox-bulk__label {
        font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.1em;
        text-transform: uppercase; color: var(--text-muted);
      }

      /* Inputs */
      .inbox-input, .inbox-select {
        padding: 8px 10px; font-size: 13px; color: var(--text);
        background: var(--surface-2); border: 1px solid var(--border);
        border-radius: 8px; font-family: inherit;
      }
      .inbox-input:focus, .inbox-select:focus {
        outline: none; border-color: var(--primary);
        box-shadow: 0 0 0 3px var(--focus-ring);
      }
      .inbox-input { min-width: 180px; }

      /* Buttons */
      .inbox-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 6px;
        padding: 8px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 600;
        font-family: inherit; cursor: pointer; white-space: nowrap;
        border: 1px solid var(--border); color: var(--text);
        background: var(--surface-2);
        transition: all .15s ease;
      }
      .inbox-btn:hover:not(:disabled) { background: var(--surface-3); border-color: var(--border-strong); }
      .inbox-btn:disabled { opacity: .45; cursor: not-allowed; }
      .inbox-btn--sm { padding: 6px 10px; font-size: 12px; }
      .inbox-btn--ghost {
        background: transparent; border-color: var(--border-subtle);
        color: var(--text-2); font-weight: 500;
      }
      .inbox-btn--ghost:hover:not(:disabled) { background: var(--surface-2); color: var(--text); }
      .inbox-btn--primary {
        color: #fff; background: var(--gradient-primary); border-color: transparent;
        box-shadow: 0 2px 8px rgba(139,92,246,0.28);
      }
      .inbox-btn--primary:hover:not(:disabled) { background: var(--gradient-primary-hover); box-shadow: 0 4px 14px rgba(139,92,246,0.4); }
      .inbox-btn--approve {
        color: #fff; background: linear-gradient(135deg, #10B981, #059669);
        border-color: transparent;
        box-shadow: 0 2px 6px rgba(16,185,129,0.28);
      }
      .inbox-btn--approve:hover:not(:disabled) { background: linear-gradient(135deg, #059669, #047857); }
      .inbox-btn--reject {
        color: var(--danger); background: transparent;
        border-color: rgba(239,68,68,0.3);
      }
      .inbox-btn--reject:hover:not(:disabled) { background: var(--danger-bg); border-color: var(--danger); color: var(--danger); }

      /* Import panel */
      .inbox-import {
        padding: 18px 20px; border-radius: var(--radius-card);
        background: var(--surface-1); border: 1px solid var(--border-subtle);
        box-shadow: var(--shadow-card);
        display: flex; flex-direction: column; gap: 10px;
      }
      html.dark .inbox-import {
        background: var(--surface-1);
        box-shadow: 0 1px 3px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04);
      }
      .inbox-import__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
      .inbox-import__head h3 { margin: 0; font-family: var(--font-display); font-size: 20px; color: var(--text); letter-spacing: var(--tracking-tight); }
      .inbox-import__head p { margin: 4px 0 0; font-size: 13px; color: var(--text-2); line-height: 1.45; }
      .inbox-import__actions { display: flex; gap: 10px; margin-top: 4px; }
      .inbox-field { display: block; }
      .inbox-field--inline { display: flex; align-items: center; gap: 10px; }
      .inbox-field__label {
        display: block; font-family: var(--font-mono); font-size: 10.5px;
        letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-muted);
        margin-bottom: 6px;
      }
      .inbox-field--inline .inbox-field__label { margin: 0; }
      .inbox-code-input {
        width: 100%; min-height: 160px; padding: 12px 14px;
        font: 12.5px/1.55 var(--font-mono, monospace);
        color: var(--text); background: var(--surface-0);
        border: 1px solid var(--border-subtle); border-radius: 10px;
        resize: vertical;
      }
      html.dark .inbox-code-input { background: #030209; }
      .inbox-code-input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }

      /* View toggle */
      .inbox-view-toggle {
        display: inline-flex;
        padding: 2px; border-radius: 8px;
        background: var(--surface-2); border: 1px solid var(--border-subtle);
      }
      .inbox-view-btn {
        display: inline-flex; align-items: center; justify-content: center;
        width: 28px; height: 26px; padding: 0;
        border: none; background: transparent; color: var(--text-muted);
        border-radius: 6px; cursor: pointer; transition: all .15s ease;
      }
      .inbox-view-btn:hover { color: var(--text-2); }
      .inbox-view-btn.is-active {
        background: var(--surface-1); color: var(--primary);
        box-shadow: 0 1px 2px rgba(0,0,0,0.06);
      }
      html.dark .inbox-view-btn.is-active { background: var(--surface-3); }
      .inbox-view-btn svg { display: block; }

      /* Items container */
      .inbox-items { display: flex; flex-direction: column; }
      .inbox-items--card { gap: 14px; }
      .inbox-items--list { gap: 4px; }

      /* Card (shared) */
      .inbox-card {
        position: relative; overflow: hidden;
        background: var(--surface-1); border: 1px solid var(--border-subtle);
        border-radius: var(--radius-card);
        transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
      }
      .inbox-items--card .inbox-card { box-shadow: var(--shadow-card); }
      html.dark .inbox-items--card .inbox-card {
        box-shadow: 0 1px 3px rgba(0,0,0,0.4), 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04);
      }
      .inbox-card:hover {
        border-color: var(--border); background: var(--surface-1);
        transform: translateY(-1px);
      }
      .inbox-card { transition: border-color .12s ease, background .12s ease, transform .12s ease, box-shadow .12s ease; }
      .inbox-card.is-high-risk {
        border-color: rgba(239,68,68,0.35);
        box-shadow: inset 3px 0 0 0 #EF4444;
      }
      html.dark .inbox-card.is-high-risk {
        border-color: rgba(239,68,68,0.45);
        box-shadow: inset 3px 0 0 0 #F87171;
      }
      .inbox-card.is-approved { opacity: 0.72; }
      .inbox-card.is-approved:hover { opacity: 1; }
      .inbox-card.is-rejected { opacity: 0.55; }
      .inbox-card.is-rejected:hover { opacity: 1; }
      .inbox-card.is-executed { opacity: 0.82; }
      .inbox-card.is-executed .inbox-source::after {
        content: ''; display: inline-block; width: 10px; height: 10px; margin-left: 2px;
        background: #10B981; border-radius: 50%;
        box-shadow: inset 0 0 0 2px var(--surface-1);
      }

      /* Row header — clickable to expand */
      .inbox-card__head {
        display: flex; flex-direction: column; gap: 6px;
        padding: 12px 16px 10px;
        cursor: pointer; user-select: none;
        min-width: 0;
      }
      .inbox-card__head:hover { background: var(--surface-2); }
      .inbox-card.is-expanded .inbox-card__head {
        border-bottom: 1px solid var(--border-subtle);
      }

      /* Line 1 — checkbox · source chip · content preview · time · chevron */
      .inbox-card__line1 {
        display: flex; align-items: center; gap: 10px; min-width: 0;
      }
      .inbox-card__preview {
        flex: 1 1 0; min-width: 0;
        color: var(--text); font-size: 14px; font-weight: 500; line-height: 1.4;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .inbox-card__chevron {
        display: inline-flex; color: var(--text-muted);
        transition: transform .2s ease, color .15s ease;
        flex-shrink: 0;
      }
      .inbox-card.is-expanded .inbox-card__chevron { transform: rotate(180deg); color: var(--primary); }
      .inbox-card__head:hover .inbox-card__chevron { color: var(--text-2); }

      /* Line 2 — meta chips (dimmer, smaller) */
      .inbox-card__line2 {
        display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
        padding-left: 28px; /* align with preview text, past the checkbox */
        min-height: 22px;
      }

      /* Body — collapsed state hides it */
      .inbox-card__body {
        padding: 12px 16px 16px;
        display: flex; flex-direction: column; gap: 12px;
      }
      .inbox-card.is-collapsed .inbox-card__body { display: none; }

      /* In list view, hide the preview in expanded rows (it's redundant with body) */
      .inbox-items--list .inbox-card.is-expanded .inbox-card__preview { display: none; }

      /* Card view specifics — roomier padding */
      .inbox-items--card .inbox-card__head { padding: 14px 18px 12px; }
      .inbox-items--card .inbox-card.is-expanded .inbox-card__preview { display: none; }
      .inbox-items--card .inbox-card__body { padding: 12px 18px 18px; }

      /* List view — tighter, denser; line 2 becomes one-line-no-wrap */
      .inbox-items--list .inbox-card { border-radius: 8px; }
      .inbox-items--list .inbox-card__head { padding: 8px 14px 7px; gap: 4px; }
      .inbox-items--list .inbox-card__line2 {
        flex-wrap: nowrap; overflow: hidden;
        font-size: 11px;
      }
      .inbox-items--list .inbox-card__line2 > .inbox-chip {
        flex-shrink: 0;
      }
      /* Hide lower-priority chips in list view to prevent overflow */
      .inbox-items--list .inbox-chip--brand { display: none; }

      .inbox-card__foot {
        display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        padding-top: 12px;
        border-top: 1px solid var(--border-subtle);
      }
      .inbox-spacer { flex: 1; }
      .inbox-time { font-size: 12px; white-space: nowrap; }

      .inbox-check--inline { margin: 0; }

      /* Icon-only buttons (trash glyph on each row footer) */
      .inbox-btn.inbox-btn--icon-only {
        padding: 6px 8px;
        min-width: 30px;
      }
      .inbox-btn--icon-only svg { display: block; }

      /* Slim bulk bar (shown on non-pending tabs when items are selected) */
      .inbox-bulk--slim {
        padding: 6px 14px;
        background: linear-gradient(135deg, var(--bg-accent-1), transparent);
        border: 1px solid rgba(139,92,246,0.18);
      }
      .inbox-bulk--slim .inbox-bulk__right { justify-content: flex-end; }

      /* Delete scope modal */
      .inbox-del-overlay {
        position: fixed; inset: 0; z-index: 10000;
        display: flex; align-items: center; justify-content: center;
        padding: 20px;
        background: rgba(0,0,0,0.45); backdrop-filter: blur(4px);
      }
      .inbox-del-modal {
        width: 100%; max-width: 480px;
        background: var(--surface-1);
        border: 1px solid var(--border-subtle);
        border-radius: var(--radius-modal, 16px);
        padding: 22px 24px 18px;
        box-shadow: var(--shadow-floating, 0 30px 80px rgba(0,0,0,0.4));
      }
      html.dark .inbox-del-modal {
        box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05);
      }
      .inbox-del-title {
        margin: 0 0 6px; font-family: var(--font-display);
        font-size: 22px; letter-spacing: var(--tracking-tight);
        color: var(--text);
      }
      .inbox-del-sub { margin: 0 0 18px; color: var(--text-2); font-size: 13.5px; line-height: 1.5; }
      .inbox-del-options { display: flex; flex-direction: column; gap: 8px; }
      .inbox-del-option {
        text-align: left;
        padding: 14px 16px;
        background: var(--surface-2);
        border: 1px solid var(--border-subtle);
        border-radius: 10px;
        cursor: pointer;
        font-family: inherit; color: var(--text);
        transition: all .15s ease;
      }
      .inbox-del-option:hover { border-color: var(--primary); background: var(--bg-accent-1); }
      .inbox-del-option__title { font-weight: 600; font-size: 14px; margin-bottom: 3px; }
      .inbox-del-option__sub { font-size: 12px; color: var(--text-2); line-height: 1.4; }
      .inbox-del-option--danger:hover {
        border-color: var(--danger);
        background: var(--danger-bg);
      }
      .inbox-del-option--danger:hover .inbox-del-option__title { color: var(--danger); }
      .inbox-del-footer {
        display: flex; justify-content: flex-end; margin-top: 14px;
      }

      /* Source chip — headline identity for the row */
      .inbox-source {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 10px; font-size: 12.5px; font-weight: 600;
        color: var(--src-color, var(--text-2));
        background: var(--src-bg, var(--surface-2));
        border: 1px solid var(--src-border, var(--border-subtle));
        border-radius: 999px;
        flex-shrink: 0;
      }
      html.dark .inbox-source {
        background: color-mix(in oklab, var(--src-bg, var(--surface-2)) 55%, transparent);
        color: var(--src-color-dark, var(--src-color, var(--text)));
      }
      .inbox-source__dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

      /* Meta chips — line 2 metadata */
      .inbox-chip {
        display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px;
        font-size: 10.5px; font-family: var(--font-mono);
        color: var(--text-2); background: transparent;
        border: 1px solid var(--border-subtle); border-radius: 5px;
        line-height: 1.5;
      }
      .inbox-chip__dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
      .inbox-chip--platform {
        font-weight: 700; color: var(--text);
        text-transform: uppercase; letter-spacing: 0.05em;
        background: var(--surface-2);
      }
      .inbox-chip--brand { font-family: inherit; font-size: 11.5px; font-weight: 500; }

      /* Scheduled chip — green dot for future, amber for overdue */
      .inbox-chip--sched { color: var(--text-2); border-color: var(--border-subtle); }
      .inbox-chip--sched--future { color: #047857; background: rgba(16,185,129,0.08); border-color: rgba(16,185,129,0.22); }
      html.dark .inbox-chip--sched--future { color: #6EE7B7; background: rgba(16,185,129,0.12); border-color: rgba(16,185,129,0.28); }
      .inbox-chip--sched--overdue { color: #92400E; background: rgba(245,158,11,0.10); border-color: rgba(245,158,11,0.28); }
      html.dark .inbox-chip--sched--overdue { color: #FCD34D; background: rgba(245,158,11,0.14); border-color: rgba(245,158,11,0.32); }

      /* Voice score — green 80+, amber 60-79, red <60 */
      .inbox-chip--voice { font-weight: 700; letter-spacing: 0.01em; }
      .inbox-chip--voice--high  { color: #047857; background: rgba(16,185,129,0.08); border-color: rgba(16,185,129,0.22); }
      html.dark .inbox-chip--voice--high { color: #6EE7B7; background: rgba(16,185,129,0.12); }
      .inbox-chip--voice--mid   { color: #92400E; background: rgba(245,158,11,0.10); border-color: rgba(245,158,11,0.28); }
      html.dark .inbox-chip--voice--mid { color: #FCD34D; background: rgba(245,158,11,0.14); }
      .inbox-chip--voice--low   { color: #991B1B; background: rgba(239,68,68,0.10); border-color: rgba(239,68,68,0.28); }
      html.dark .inbox-chip--voice--low { color: #FCA5A5; background: rgba(239,68,68,0.15); }

      /* Risk chip inline on line 2 — short summary; full list lives in body */
      .inbox-chip--risk {
        color: #991B1B; background: rgba(239,68,68,0.08);
        border-color: rgba(239,68,68,0.28); font-weight: 600;
      }
      html.dark .inbox-chip--risk { color: #FCA5A5; background: rgba(239,68,68,0.15); }

      /* Group header — "6 drafts from Claude.ai" with bulk actions */
      .inbox-group-head {
        display: flex; align-items: center; justify-content: space-between;
        gap: 12px; padding: 10px 14px;
        background: var(--surface-2); border: 1px solid var(--border-subtle);
        border-radius: 10px;
        margin: 8px 0 4px;
        position: sticky; top: 0; z-index: 2;
      }
      .inbox-group-head__label { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text); font-weight: 500; }
      .inbox-group-head__count { font-family: var(--font-mono); font-weight: 700; color: var(--text); }
      .inbox-group-head__name { font-weight: 600; }
      .inbox-group-head__hint { color: var(--text-muted); font-size: 12px; }
      .inbox-group-head__actions { display: inline-flex; gap: 6px; }

      .inbox-violations { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
      .inbox-violation {
        padding: 4px 10px; font-size: 11.5px; font-weight: 600;
        color: #9f1239; background: rgba(239,68,68,0.08);
        border: 1px solid rgba(239,68,68,0.25); border-radius: 6px;
      }
      html.dark .inbox-violation { color: #fecaca; background: rgba(239,68,68,0.15); }

      .inbox-content {
        white-space: pre-wrap; word-break: break-word;
        font-family: var(--font-body); font-size: 14px; line-height: 1.55;
        color: var(--text);
        padding: 14px 16px;
        background: var(--surface-2);
        border-radius: 10px;
      }
      .inbox-edit-textarea {
        width: 100%; min-height: 140px; padding: 14px 16px;
        font-family: var(--font-body); font-size: 14px; line-height: 1.55;
        color: var(--text); background: var(--surface-2);
        border: 1px solid var(--primary); border-radius: 10px;
        resize: vertical;
        box-shadow: 0 0 0 3px var(--focus-ring);
      }
      .inbox-edit-textarea:focus { outline: none; }

      /* Phase D1 — Brain-write proposal rendering */
      .inbox-brainwrite {
        display: flex; flex-direction: column; gap: 12px;
        padding: 14px 16px; border-radius: 10px;
        background: var(--surface-2);
        color: var(--text); font-size: 14px; line-height: 1.55;
      }
      .bw-proposal {
        padding: 12px 14px; border-radius: 8px;
        background: rgba(139,92,246,0.08);
        border-left: 3px solid rgba(139,92,246,0.5);
        white-space: pre-wrap; word-break: break-word;
      }
      .bw-proposal--removed {
        background: rgba(239,68,68,0.06);
        border-left-color: rgba(239,68,68,0.55);
        text-decoration: line-through;
        color: var(--text2);
      }
      .bw-row { display: flex; gap: 10px; align-items: baseline; font-size: 13px; }
      .bw-row__label {
        flex-shrink: 0; width: 80px;
        font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
        color: var(--text2); padding-top: 2px;
      }
      .bw-row__value { color: var(--text); word-break: break-word; }
      .bw-row--source .bw-row__value {
        font-family: ui-monospace, SFMono-Regular, monospace;
        font-size: 12px; color: var(--text2);
      }
      .bw-diff { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      .bw-diff__col {
        padding: 10px 12px; border-radius: 8px;
        background: var(--surface-1); border: 1px solid var(--border-subtle);
      }
      .bw-diff__label {
        font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em;
        color: var(--text2); margin-bottom: 4px;
      }
      .bw-diff__body { font-size: 13px; color: var(--text); white-space: pre-wrap; word-break: break-word; }
      .bw-rating { font-size: 18px; }
      .bw-post-preview {
        padding: 10px 12px; border-radius: 8px;
        background: var(--surface-1); border: 1px solid var(--border-subtle);
        font-style: italic; color: var(--text2); font-size: 13px;
      }
      @media (max-width: 640px) {
        .bw-diff { grid-template-columns: 1fr; }
      }

      /* Approval-mode popover — compact menu in the toolbar that mirrors the
         Settings → AI radio group, backed by the same endpoint. */
      .inbox-approval { position: relative; display: inline-flex; }
      .inbox-approval__btn { display: inline-flex; align-items: center; gap: 6px; }
      .inbox-approval__btn.is-open {
        border-color: var(--primary);
        background: rgba(139,92,246,0.08);
      }
      .inbox-approval__dot {
        width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
        box-shadow: 0 0 0 2px rgba(255,255,255,0.08);
      }
      .inbox-approval__dot--review { background: #F59E0B; }
      .inbox-approval__dot--instant { background: #10B981; }
      .inbox-approval__label { font-size: 12px; font-weight: 500; }

      .inbox-approval__popover {
        position: absolute; top: calc(100% + 6px); right: 0;
        min-width: 340px; max-width: 380px;
        padding: 14px; z-index: 40;
        background: var(--surface-1, #fff);
        border: 1px solid var(--border);
        border-radius: 12px;
        box-shadow: 0 18px 40px -14px rgba(0,0,0,0.35), 0 3px 8px -2px rgba(0,0,0,0.12);
        display: flex; flex-direction: column; gap: 10px;
      }
      html.dark .inbox-approval__popover {
        background: var(--surface-1, #12121e);
        box-shadow: 0 18px 40px -14px rgba(0,0,0,0.6), 0 3px 8px -2px rgba(0,0,0,0.3);
      }
      .inbox-approval__header { display: flex; flex-direction: column; gap: 2px; margin-bottom: 4px; }
      .inbox-approval__title { font-size: 13px; font-weight: 600; color: var(--text); }
      .inbox-approval__hint { font-size: 11px; color: var(--text2); line-height: 1.4; }

      .inbox-approval__option {
        display: flex; align-items: flex-start; gap: 10px;
        padding: 10px 12px; border-radius: 9px;
        border: 1px solid var(--border);
        background: var(--surface-2, rgba(255,255,255,0.02));
        cursor: pointer;
        transition: border-color 0.15s, background 0.15s;
      }
      .inbox-approval__option:hover { border-color: var(--text2); }
      .inbox-approval__option.is-active {
        border-color: var(--primary);
        background: rgba(139,92,246,0.06);
      }
      .inbox-approval__option input[type="radio"] {
        margin-top: 3px; flex-shrink: 0; accent-color: var(--primary);
      }
      .inbox-approval__option-body { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
      .inbox-approval__option-title {
        font-size: 13px; font-weight: 600; color: var(--text);
        display: inline-flex; align-items: center; gap: 6px;
      }
      .inbox-approval__option-tag {
        display: inline-flex; align-items: center;
        padding: 1px 7px; border-radius: 999px;
        font-size: 10px; font-weight: 500;
        background: rgba(139,92,246,0.14); color: #A78BFA;
        text-transform: lowercase; letter-spacing: 0.01em;
      }
      .inbox-approval__option-desc { font-size: 12px; line-height: 1.45; color: var(--text2); }

      .inbox-approval__status {
        padding: 6px 10px; border-radius: 7px; font-size: 12px; font-weight: 500;
        text-align: center;
      }
      .inbox-approval__status--ok { background: rgba(16,185,129,0.10); color: #10B981; }
      .inbox-approval__status--pending { background: var(--surface-2); color: var(--text2); }
      .inbox-approval__status--err { background: rgba(239,68,68,0.10); color: #EF4444; }

      @media (max-width: 560px) {
        .inbox-approval__popover { right: auto; left: 0; min-width: 280px; }
      }

      .inbox-grounded {
        margin-top: 12px; padding: 10px 14px;
        background: var(--bg-accent-1); border: 1px dashed rgba(139,92,246,0.25);
        border-radius: 8px;
      }
      .inbox-grounded > div { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 6px; }
      .inbox-grounded__pill {
        display: inline-flex; align-items: center; gap: 4px;
        padding: 3px 8px; font-size: 10.5px; font-family: var(--font-mono);
        color: var(--text); background: var(--surface-1);
        border: 1px solid var(--border-subtle); border-radius: 5px;
      }
      .inbox-grounded__kind {
        font-size: 9.5px; color: var(--primary); font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.08em;
        border-right: 1px solid var(--border-subtle); padding-right: 5px;
      }
      .inbox-grounded__empty { font-style: italic; color: var(--text-muted); font-size: 12px; }

      .inbox-check {
        display: inline-flex; align-items: center; gap: 6px;
        font-size: 12.5px; font-weight: 500; color: var(--text-2); cursor: pointer;
        user-select: none;
      }
      .inbox-check input {
        width: 16px; height: 16px; accent-color: var(--primary); cursor: pointer;
      }
      .inbox-decided { font-size: 12px; color: var(--text-2); }

      /* Status badge (for non-pending items) */
      .inbox-status {
        display: inline-block; padding: 2px 10px;
        font-size: 11px; font-weight: 600; font-family: var(--font-mono);
        border-radius: 999px; text-transform: uppercase; letter-spacing: 0.05em;
      }
      .inbox-status--approved { color: var(--success); background: var(--success-bg); }
      .inbox-status--rejected { color: var(--danger); background: var(--danger-bg); }
      .inbox-status--executed { color: var(--primary); background: var(--bg-accent-1); }

      /* Loading skeleton */
      .inbox-skeleton {
        height: 140px; border-radius: var(--radius-card);
        background: linear-gradient(90deg, var(--surface-1), var(--surface-2), var(--surface-1));
        background-size: 200% 100%;
        animation: inbox-shimmer 1.4s ease-in-out infinite;
      }
      @keyframes inbox-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      .inbox-error {
        padding: 14px 18px; border-radius: 10px;
        color: var(--danger); background: var(--danger-bg);
        border: 1px solid rgba(239,68,68,0.25);
      }

      /* Empty state — content anchored near the top with the halo behind it.
         Previously used justify-content: center inside a tall flex parent,
         which made the icon+title feel like they'd fallen to the bottom of
         the page. Anchoring to the top with generous top padding reads as
         deliberate and keeps the content grouped with the halo. */
      .inbox-empty {
        position: relative; overflow: hidden;
        text-align: center; padding: 72px 24px 56px;
        border-radius: var(--radius-card);
        background: linear-gradient(135deg, var(--surface-1), var(--surface-2));
        border: 1px dashed var(--border-subtle);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
      }
      .inbox-empty__halo {
        position: absolute;
        top: 48px; left: 50%; transform: translateX(-50%);
        width: 220px; height: 140px; border-radius: 50%;
        background: radial-gradient(circle at center, rgba(139,92,246,0.22), transparent 65%);
        filter: blur(14px); pointer-events: none;
      }
      html.dark .inbox-empty__halo { background: radial-gradient(circle at center, rgba(139,92,246,0.28), transparent 65%); }
      /* Stack content above the halo blur — exclude the halo itself so its
         position:absolute stays respected (otherwise the halo got stacked
         inline and pushed the icon/title down). */
      .inbox-empty > *:not(.inbox-empty__halo) { position: relative; }
      .inbox-empty__icon {
        display: inline-flex; align-items: center; justify-content: center;
        width: 64px; height: 64px; margin: 0 auto 16px;
        border-radius: 50%;
        background: var(--bg-accent-1);
        color: var(--primary);
      }
      .inbox-empty__icon svg { display: block; }
      .inbox-empty__title { font-family: var(--font-display); font-size: 24px; color: var(--text); letter-spacing: var(--tracking-tight); }
      .inbox-empty__sub { margin-top: 6px; color: var(--text-2); font-size: 14px; }
      .inbox-empty__cta { margin-top: 18px; }
    </style>`;
}

async function fetchJson(url, opts = {}) {
    const csrf = document.cookie.split('; ').find(c => c.startsWith('csrf_token='))?.split('=')[1] || '';
    const init = {
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
    };
    if (opts.method) init.method = opts.method;
    if (opts.body) init.body = JSON.stringify(opts.body);
    const r = await fetch(url, init);
    const text = await r.text();
    let data = null;
    try { data = text ? JSON.parse(text) : {}; } catch (_) { }
    if (!r.ok) throw new Error((data && (data.error?.message || data.error)) || `HTTP ${r.status}`);
    return data;
}

function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}
